import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useRef, useState, useEffect, useMemo } from "react";
import {
  useContractInboxDetail,
  useRejectContract,
  useSignContract,
  useUploadAttachment,
} from "~/services/customer/contractRequest";
import type { CdAttachType } from "~/types/common";
import RichContractRenderer from "@/common/RichContractRenderer";
import type { ContractProgress } from "@/common/RichContractRenderer";
import {
  ContractRichEditor,
  type RichEditorHandle,
} from "@/business/contract/ContractRichEditor";
import RenderedContractRenderer from "@/common/RenderedContractRenderer";
import type { RenderedContractRendererRef } from "@/common/RenderedContractRenderer";
import { useNavVisibilityStore } from "&/navVisibility";

export const Route = createFileRoute("/customer/layout/contract-sign")({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): { requestSeq?: string } => ({
    requestSeq: search.requestSeq ? String(search.requestSeq) : undefined,
  }),
});

// 헤더용 짧은 포맷: "7/30 16:54"
const formatShortDateTime = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso.includes("T") ? iso : String(iso).replace(" ", "T"));
  if (isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

type FieldType = "signature" | "checkbox" | "text" | "date";

interface ContractField {
  id: string;
  type: FieldType;
  label: string;
  required: boolean;
  value?: string | boolean;
}

const MOCK_CONTRACT_FALLBACK = {
  body: `[반려동물 위탁 계약서]

1. 보호자 정보
- 이름: 홍길동
- 연락처: 010-1234-5678
- 반려동물: 감자 (포메라니안)

2. 위탁 기간
- 시작일: 2026-05-15
- 종료일: 2026-12-31

3. 위탁료
- 월 50,000원 (선납)

4. 보호자 의무
- 반려동물의 건강 상태를 사실대로 알려주세요.
- 예방접종 증명서를 제출해 주세요.
- 위탁 기간 중 연락이 닿을 수 있어야 합니다.

5. 사업장 의무
- 반려동물의 안전과 건강을 최우선으로 합니다.
- 매일 알림장을 통해 상태를 공유합니다.
- 응급 상황 발생 시 즉시 연락드립니다.

6. 환불 및 해지
- 위탁 시작 7일 전까지: 전액 환불
- 위탁 시작 후: 잔여 일수 기준 50%

7. 면책 사항
- 보호자가 사전에 고지하지 않은 질병 또는 행동 문제로 인한 사고
- 천재지변 등 불가항력적 사유

위 내용에 동의하시면 아래에 서명해 주세요.`,
};

/**
 * docJson 에 inline contractField 노드가 하나라도 있으면 true(단일레이어 계약).
 * → 편집기와 동일한 docJson 렌더러(RichContractRenderer)로 본문+서명 처리(baked 불필요).
 * 레거시(필드가 manifest 에만) 계약은 false → baked 경로 유지.
 */
function docHasInlineFields(raw?: string | null): boolean {
  if (!raw) return false;
  try {
    const doc = JSON.parse(raw);
    let found = false;
    const walk = (n: any) => {
      if (found || !n || typeof n !== "object") return;
      if (n.type === "contractField") {
        found = true;
        return;
      }
      if (Array.isArray(n.content)) for (const k of n.content) walk(k);
    };
    walk(doc);
    return found;
  } catch {
    return false;
  }
}

function RouteComponent() {
  const { requestSeq } = Route.useSearch();
  const { history } = useRouter();
  const seqNum = requestSeq ? Number(requestSeq) : undefined;
  const { data: detail } = useContractInboxDetail(seqNum);
  const signMutation = useSignContract();
  const rejectMutation = useRejectContract();
  const uploadAttachMutation = useUploadAttachment();
  const renderedRendererRef = useRef<RenderedContractRendererRef>(null);
  const signEditorRef = useRef<RichEditorHandle>(null);
  const [renderedProgress, setRenderedProgress] =
    useState<ContractProgress | null>(null);
  // 선택(비필수) 필드 진행 현황 — 제출 모달에 "필수/선택" 표시용. 제출은 막지 않는다.
  const [signOptional, setSignOptional] = useState<{
    done: number;
    total: number;
  }>({ done: 0, total: 0 });

  // 서명은 집중 플로우 — 하단 앱 탭바를 숨겨 제출 액션 독이 하단을 온전히 차지하게 한다.
  const setNavVisible = useNavVisibilityStore((s) => s.setVisible);
  useEffect(() => {
    setNavVisible(false);
    return () => setNavVisible(true);
  }, [setNavVisible]);

  const c = useMemo(
    () => ({
      requestSeq: seqNum ?? 0,
      storeName: detail?.companyName ?? "—",
      storeContact: "—",
      templateName: detail?.contractName ?? "—",
      message: detail?.pushMessage,
      expiresAt: detail?.dtExpired ?? "",
      body: detail?.contractContents ?? MOCK_CONTRACT_FALLBACK.body,
      docJson: detail?.contractDocJson ?? null,
      renderedPageFileUrls: detail?.renderedPageFileUrls ?? null,
      placementManifest: detail?.placementManifest ?? null,
      sealImageUrl: detail?.sealImageUrl ?? null,
      variables: detail?.variables ?? {},
      signatureUrl: detail?.signatureUrl ?? null,
    }),
    [detail, seqNum],
  );
  // 단일레이어(신규) 계약 = docJson 에 inline contractField 존재 → 편집기와 동일한
  // docJson 렌더러로 본문+서명. baked PNG 는 레거시 계약에만 사용(stale/누락 회피).
  const preferDoc = useMemo(() => docHasInlineFields(c.docJson), [c.docJson]);
  // ContractRichEditor(signMode)용 파싱된 docJson.
  const docJsonObj = useMemo(() => {
    if (!c.docJson) return null;
    try {
      return JSON.parse(c.docJson);
    } catch {
      return null;
    }
  }, [c.docJson]);
  const hasRenderedContract =
    !preferDoc && !!(c.renderedPageFileUrls?.length && c.placementManifest);
  // 렌더러가 필드 상태를 소유하는 경로(docJson or baked).
  const hasInteractiveRenderer = preferDoc || hasRenderedContract;

  const [fields, setFields] = useState<ContractField[]>([
    {
      id: "agree1",
      type: "checkbox",
      label: "위 계약 내용을 모두 확인했어요",
      required: true,
    },
    {
      id: "agree2",
      type: "checkbox",
      label: "예방접종 증명서를 제출하는 데 동의해요",
      required: true,
    },
    {
      id: "agree3",
      type: "checkbox",
      label: "마케팅 정보 수신에 동의해요 (선택)",
      required: false,
    },
    { id: "sign", type: "signature", label: "보호자 서명", required: true },
  ]);

  const [showSignModal, setShowSignModal] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // 첨부파일 (예방접종증명서 등)
  type AttachKind = "VACCINATION" | "HEALTH_CHECK" | "ID_CARD" | "OTHER";
  interface AttachItem {
    id: string;
    kind: AttachKind;
    name: string;
    size: number;
    dataUrl: string;
    file: File;
  }
  const ATTACH_LABEL: Record<AttachKind, string> = {
    VACCINATION: "예방접종 증명서",
    HEALTH_CHECK: "건강검진 결과",
    ID_CARD: "신분증",
    OTHER: "기타",
  };
  const [attachments, setAttachments] = useState<AttachItem[]>([]);
  const [attachKind, setAttachKind] = useState<AttachKind>("VACCINATION");
  const [attachOpen, setAttachOpen] = useState(true);

  const updateField = (id: string, value: string | boolean) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, value } : f)));
  };

  // 렌더 계약서는 렌더러가 필드 상태를 갖는다(진행률로 게이트). 순수 텍스트만 레거시 동의 카드.
  const requiredAllFilled = hasInteractiveRenderer
    ? renderedProgress
      ? renderedProgress.done >= renderedProgress.total
      : true
    : fields
        .filter((f) => f.required)
        .every((f) => (f.type === "checkbox" ? f.value === true : !!f.value));

  // 하단 제출 버튼: 죽은 회색 대신 항상 살아있게 — 미완료면 "남은 항목 입력하기"(해당 필드로 이동)
  const renderedRemaining =
    hasInteractiveRenderer && renderedProgress
      ? Math.max(0, renderedProgress.total - renderedProgress.done)
      : 0;
  const needsMoreInput = hasInteractiveRenderer && renderedRemaining > 0;

  // 활성 렌더러 가이드 입력(미완료 필드로 이동/팝업). baked=startGuidedInput / docJson(TipTap)=scrollToNextIncomplete.
  const guideToNextField = () => {
    if (preferDoc) signEditorRef.current?.scrollToNextIncomplete();
    else renderedRendererRef.current?.startGuidedInput();
  };

  // 이미 처리된(완료/서명/거절/만료/취소) 계약은 재서명·재거절 불가.
  // SENT/OPENED(또는 아직 로딩 전 null)만 서명 가능 → 그 외엔 버튼 대신 상태 안내.
  const requestStatus = detail?.cdRequestStatus ?? null;
  const alreadyProcessed =
    requestStatus != null &&
    requestStatus !== "REQUEST_STATUS_SENT" &&
    requestStatus !== "REQUEST_STATUS_OPENED";
  const processedLabel =
    requestStatus === "REQUEST_STATUS_COMPLETED" ||
    requestStatus === "REQUEST_STATUS_SIGNED"
      ? "이미 서명을 완료한 계약이에요"
      : requestStatus === "REQUEST_STATUS_REJECTED"
        ? "거절한 계약이에요"
        : requestStatus === "REQUEST_STATUS_EXPIRED"
          ? "기한이 만료된 계약이에요"
          : requestStatus === "REQUEST_STATUS_CANCELED"
            ? "사업장이 취소한 계약이에요"
            : "이미 처리된 계약이에요";

  const handleSubmit = async () => {
    if (!seqNum) return;
    if (alreadyProcessed) {
      alert(processedLabel);
      return;
    }
    // 필수 미완료여도 모달은 연다 — 모달 안에서 필수/선택 현황을 보여주고,
    // 필수 미완료면 제출 버튼만 비활성. (네이티브 confirm/alert 은 인앱 브라우저에서 차단됨)
    setShowSubmitConfirm(true);
  };

  const doSubmit = async () => {
    if (!seqNum) return;
    setShowSubmitConfirm(false);

    const fieldValues: Record<string, string> = {};
    let signatureDataUrl = "";

    if (hasInteractiveRenderer) {
      const validationError = preferDoc
        ? signEditorRef.current?.validateSign()
        : renderedRendererRef.current?.validate();
      if (validationError) {
        alert(validationError);
        return;
      }
      const values = preferDoc
        ? signEditorRef.current?.getSignValues()
        : renderedRendererRef.current?.getValues();
      Object.entries(values?.fields ?? {}).forEach(([key, value]) => {
        if (value) fieldValues[key] = value;
      });
      Object.entries(values?.checkboxes ?? {}).forEach(([key, value]) => {
        fieldValues[key] = value ? "Y" : "N";
      });
      signatureDataUrl = values?.signature ?? "";
    } else {
      const signField = fields.find((f) => f.type === "signature");
      signatureDataUrl = (signField?.value as string) || "";
      fields.forEach((f) => {
        if (f.type === "checkbox")
          fieldValues[f.id] = f.value === true ? "Y" : "N";
        else if (f.type === "text" || f.type === "date") {
          if (typeof f.value === "string" && f.value)
            fieldValues[f.id] = f.value;
        }
      });
    }

    if (!signatureDataUrl) {
      alert("서명을 해주세요.");
      return;
    }

    // 서명 확정 직전에만 첨부 업로드 → 서명하지 않고 이탈하면 첨부가 남지 않는다(orphan 방지).
    if (attachments.length > 0) {
      try {
        for (const a of attachments) {
          await uploadAttachMutation.mutateAsync({
            requestSeq: seqNum,
            file: a.file,
            attachType: ("ATTACH_TYPE_" + a.kind) as CdAttachType,
          });
        }
      } catch (err) {
        console.error(err);
        alert("첨부파일 업로드에 실패했어요. 다시 시도해 주세요.");
        return;
      }
    }

    signMutation.mutate(
      { requestSeq: seqNum, signatureDataUrl, fieldValues },
      {
        onSuccess: () => {
          alert("서명이 완료됐어요. 사업장에 전달됩니다.");
          if (window.history.length > 1) history.back();
          else history.replace("/customer/layout/contract-inbox" as any);
        },
        onError: (e) => {
          console.error(e);
          alert("서명 제출 실패. 다시 시도해 주세요.");
        },
      },
    );
  };

  const handleReject = () => {
    if (!seqNum) return;
    if (!rejectReason.trim()) {
      alert("거절 사유를 적어주세요");
      return;
    }
    rejectMutation.mutate(
      { requestSeq: seqNum, reason: rejectReason },
      {
        onSuccess: () => {
          alert("거절이 전달됐어요");
          setShowRejectModal(false);
          if (window.history.length > 1) history.back();
          else history.replace("/customer/layout/contract-inbox" as any);
        },
        onError: (e) => {
          console.error(e);
          alert("거절 처리 실패. 다시 시도해 주세요.");
        },
      },
    );
  };

  const attachmentSection = (
    <div className="mx-4 my-3 overflow-hidden rounded-2xl border border-[#EEE] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      <button
        type="button"
        onClick={() => setAttachOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3.5 text-left"
      >
        <span className="flex items-center gap-2 text-[13px] font-bold text-[#333]">
          📎 첨부파일{" "}
          <span className="text-[11px] font-medium text-[#AAA]">선택</span>
          {attachments.length > 0 && (
            <span className="rounded-full bg-[#FFF3E5] px-2 py-0.5 text-[11px] font-bold text-[#E08600]">
              {attachments.length}
            </span>
          )}
        </span>
        <span
          className={`text-[#BBB] transition-transform ${attachOpen ? "rotate-180" : ""}`}
        >
          ⌄
        </span>
      </button>
      {attachOpen && (
        <div className="px-4 pb-4">
          <div className="mb-2 text-[11px] text-[#666]">
            예방접종 증명서 등 사업장에 함께 전달할 자료가 있으면 첨부해 주세요
          </div>
          <div className="mb-2 grid grid-cols-4 gap-1.5">
            {(Object.keys(ATTACH_LABEL) as AttachKind[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setAttachKind(k)}
                className={`rounded-full border py-1.5 text-[10px] ${
                  attachKind === k
                    ? "border-[#FF9934] bg-[#FFF7E6] font-semibold text-[#FF9934]"
                    : "border-[#E9E9E9] bg-white text-[#666]"
                }`}
              >
                {ATTACH_LABEL[k]}
              </button>
            ))}
          </div>
          <label className="block w-full cursor-pointer rounded-lg border-2 border-dashed border-[#E9E9E9] py-3 text-center text-[12px] text-[#666] hover:border-[#FF9934]">
            <input
              type="file"
              accept="image/*,.pdf"
              hidden
              onClick={() => {
                (window as any).__mungFileChooserKind = "contract-attachment";
              }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f || !seqNum) return;
                if (f.size > 10 * 1024 * 1024) {
                  alert("10MB 이하 파일만 첨부할 수 있어요");
                  return;
                }
                // 즉시 업로드하지 않고 클라이언트에만 담아둔다 → 실제 업로드는 서명 제출 시점(handleSubmit).
                const reader = new FileReader();
                reader.onload = (ev) => {
                  setAttachments((prev) => [
                    ...prev,
                    {
                      id: `${Date.now()}-${Math.random()}`,
                      kind: attachKind,
                      name: f.name,
                      size: f.size,
                      dataUrl: ev.target?.result as string,
                      file: f,
                    },
                  ]);
                };
                reader.readAsDataURL(f);
                e.target.value = "";
              }}
            />
            📎 {ATTACH_LABEL[attachKind]} 파일 선택
          </label>
          {attachments.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {attachments.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-2 rounded bg-[#FAFAFA] p-2"
                >
                  <span className="rounded bg-[#FFF7E6] px-1.5 py-0.5 text-[10px] font-bold text-[#FF9934]">
                    {ATTACH_LABEL[a.kind]}
                  </span>
                  <span className="flex-1 truncate text-[11px]">{a.name}</span>
                  <span className="text-[10px] text-[#999]">
                    {(a.size / 1024).toFixed(1)} KB
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setAttachments(attachments.filter((x) => x.id !== a.id))
                    }
                    className="px-1 text-[12px] text-[#F24747]"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <LayoutHeader
        title="계약서 서명"
        hasBackBtn
        hasFootPrint={false}
        rightContent={
          c.expiresAt ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF0F0] px-2 py-0.5 text-[10px] font-semibold text-[#E5484D]">
              ⏰ {formatShortDateTime(c.expiresAt)}
            </span>
          ) : undefined
        }
      />
      <div className="flex min-h-0 flex-auto flex-col bg-[#F7F7F8]">
        {/* 상단 고정 영역(스크롤 안 함): 발신 메모 + 첨부 */}
        <div className="shrink-0">
          {c.message && (
            <div className="mx-4 mb-2 rounded-xl border border-[#EEE] bg-white px-3 py-2.5 text-[12.5px] leading-relaxed text-[#555]">
              “{c.message}”
            </div>
          )}

          {/* 첨부파일 — 계약서 위, 기본 접힘 */}
          {attachmentSection}
        </div>

        {/* 계약 내용 = 남은 공간 채우고 여기만 스크롤 (페이지 이중 스크롤 방지) */}
        <div className="flex min-h-0 flex-1 flex-col px-4 pb-2">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[#EEE] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <div className="flex shrink-0 items-center gap-2 border-b border-[#F0F0F0] px-4 py-2.5">
              <span className="h-3.5 w-1 rounded-full bg-[#FF9934]" />
              <div className="text-[13px] font-bold text-[#333]">계약 내용</div>
            </div>
            {preferDoc ? (
              /* 단일레이어 계약 — 편집기 그 컴포넌트(ContractRichEditor)를 readOnly+signMode 로
                 재사용. pagination·normalize·CSS 가 편집기와 100% 동일 → 페이지분할·날짜·모양 자동 일치.
                 서명 필드만 인터랙티브(SignFieldNodeView). 자체 PanZoom 스크롤 보유. */
              <div className="flex min-h-0 flex-1 flex-col">
                <ContractRichEditor
                  ref={signEditorRef}
                  initialJson={docJsonObj}
                  readOnly
                  signMode
                  signReadOnly={alreadyProcessed}
                  signValues={c.variables}
                  sealImageUrl={c.sealImageUrl}
                  signSubmitted={
                    alreadyProcessed
                      ? { signatureUrl: c.signatureUrl, values: c.variables }
                      : undefined
                  }
                  onSignProgressChange={(p) => {
                    setRenderedProgress({
                      done: p.done,
                      total: p.total,
                      items: [],
                    });
                    setSignOptional({
                      done: p.optionalDone,
                      total: p.optionalTotal,
                    });
                  }}
                />
              </div>
            ) : hasRenderedContract ? (
              <RenderedContractRenderer
                ref={renderedRendererRef}
                pageUrls={c.renderedPageFileUrls ?? []}
                placementManifest={c.placementManifest}
                variables={c.variables}
                sealImageUrl={c.sealImageUrl}
                onProgressChange={setRenderedProgress}
                fillHeight
                className="min-h-0 flex-1 px-2 pb-1"
              />
            ) : (
              <div className="min-h-0 flex-1 overflow-auto p-4">
                {c.docJson ? (
                  <RichContractRenderer
                    contractDocJson={c.docJson}
                    variables={c.variables}
                    sealImageUrl={c.sealImageUrl}
                    readOnly
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-[12px] leading-[1.7] text-[#333]">
                    {c.body}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 동의 / 서명 (순수 텍스트 레거시 경로 — 렌더러가 필드를 소유하지 않을 때만) */}
        {!hasInteractiveRenderer && (
          <div
            className="shrink-0 overflow-auto px-1"
            style={{ maxHeight: "35vh" }}
          >
            <Card title="동의 및 서명">
              <div className="space-y-3">
                {fields.map((f) =>
                  f.type === "checkbox" ? (
                    <label
                      key={f.id}
                      className="flex items-start gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={!!f.value}
                        onChange={(e) => updateField(f.id, e.target.checked)}
                        className="mt-1 accent-[#FF9934]"
                      />
                      <span className="text-[13px] text-[#333] flex-1">
                        {f.label}
                        {f.required && (
                          <span className="text-[#F24747] ml-1">*</span>
                        )}
                      </span>
                    </label>
                  ) : f.type === "signature" ? (
                    <div key={f.id}>
                      <div className="text-[12px] text-[#666] mb-1.5">
                        {f.label}
                        {f.required && (
                          <span className="text-[#F24747] ml-1">*</span>
                        )}
                      </div>
                      {f.value ? (
                        <div className="flex items-center gap-2">
                          <img
                            src={f.value as string}
                            alt="서명"
                            className="border border-[#E9E9E9] rounded bg-white h-20"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSignModal(true)}
                            className="text-[11px] text-[#FF9934] underline"
                          >
                            다시 서명
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowSignModal(true)}
                          className="w-full py-8 border-2 border-dashed border-[#FF9934] rounded-lg text-[13px] text-[#FF9934] font-semibold bg-[#FFF7E6]"
                        >
                          ✍️ 여기를 눌러 서명해 주세요
                        </button>
                      )}
                    </div>
                  ) : null,
                )}
              </div>
            </Card>
          </div>
        )}

        {/* 하단 액션 독 — 컬럼 하단 고정(스크롤 안 함), 얇은 진행바 + 거절/제출 */}
        <div
          className="shrink-0 border-t border-[#EEE] bg-white/95 px-3 pt-2.5 backdrop-blur"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)",
          }}
        >
          {hasInteractiveRenderer &&
            renderedProgress &&
            renderedProgress.total > 0 && (
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={`shrink-0 text-[11px] font-bold ${
                    renderedRemaining === 0
                      ? "text-[#22A55B]"
                      : "text-[#E08600]"
                  }`}
                >
                  {renderedRemaining === 0
                    ? "작성 완료 ✓"
                    : `입력 ${renderedProgress.done}/${renderedProgress.total}`}
                </span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#F0F0F0]">
                  <div
                    className={`h-full rounded-full transition-all ${
                      renderedRemaining === 0 ? "bg-[#22A55B]" : "bg-[#FF9934]"
                    }`}
                    style={{
                      width: `${Math.round((renderedProgress.done / renderedProgress.total) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}
          {alreadyProcessed ? (
            <div className="rounded-2xl bg-[#F5F5F5] py-3 text-center text-[13px] font-semibold text-[#888]">
              {requestStatus === "REQUEST_STATUS_COMPLETED" ||
              requestStatus === "REQUEST_STATUS_SIGNED"
                ? "✅ "
                : ""}
              {processedLabel}
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowRejectModal(true)}
                className="flex-1 rounded-2xl border border-[#E9E9E9] py-3 text-[13px] font-semibold text-[#888]"
              >
                거절
              </button>
              <button
                type="button"
                onClick={() => {
                  if (hasInteractiveRenderer && needsMoreInput) {
                    // 미완료 항목을 순차로: 해당 필드로 이동 + 입력 팝업 → 다음 항목 자동
                    guideToNextField();
                    return;
                  }
                  handleSubmit();
                }}
                disabled={!hasInteractiveRenderer && !requiredAllFilled}
                className={`flex-[2] rounded-2xl py-3 text-[14px] font-bold ${
                  hasInteractiveRenderer && needsMoreInput
                    ? "bg-[#FFE7CC] text-[#B26A00]"
                    : "bg-[#FF9934] text-white disabled:bg-[#E9E9E9] disabled:text-[#999]"
                }`}
              >
                {hasInteractiveRenderer
                  ? needsMoreInput
                    ? `✍️ 남은 항목 ${renderedRemaining}개 입력하기`
                    : "✅ 서명하고 제출하기"
                  : "서명하고 제출하기"}
              </button>
            </div>
          )}
        </div>
      </div>

      {showSignModal && (
        <SignatureModal
          onClose={() => setShowSignModal(false)}
          onSave={(dataUrl) => {
            updateField("sign", dataUrl);
            setShowSignModal(false);
          }}
        />
      )}

      {showRejectModal && (
        <RejectModal
          reason={rejectReason}
          setReason={setRejectReason}
          onClose={() => setShowRejectModal(false)}
          onSubmit={handleReject}
        />
      )}

      {showSubmitConfirm && (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 px-6"
          onClick={() => setShowSubmitConfirm(false)}
        >
          <div
            className="w-full max-w-[320px] rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 text-[15px] font-bold text-[#333]">
              서명 제출
            </div>
            <div className="mb-4 text-[13px] leading-relaxed text-[#666]">
              이대로 서명하고 제출할까요?
              <br />
              제출 후에는 수정할 수 없어요.
            </div>
            {renderedProgress && (
              <div className="mb-5 space-y-1.5 rounded-xl bg-[#FAFAFA] p-3 text-[12px]">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#333]">필수 항목</span>
                  <span
                    className={
                      renderedProgress.done >= renderedProgress.total
                        ? "font-bold text-[#22A45D]"
                        : "font-bold text-[#E5484D]"
                    }
                  >
                    {renderedProgress.done}/{renderedProgress.total} 완료
                  </span>
                </div>
                {signOptional.total > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#999]">선택 항목</span>
                    <span className="text-[#999]">
                      {signOptional.done}/{signOptional.total} · 미입력 시 생략
                    </span>
                  </div>
                )}
              </div>
            )}
            {!requiredAllFilled && (
              <div className="mb-3 text-[12px] font-medium text-[#E5484D]">
                필수 항목을 모두 채워야 제출할 수 있어요.
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-xl border border-[#EEE] py-2.5 text-[14px] font-medium text-[#666]"
                onClick={() => setShowSubmitConfirm(false)}
              >
                취소
              </button>
              {requiredAllFilled ? (
                <button
                  type="button"
                  className="flex-1 rounded-xl bg-[#FF9934] py-2.5 text-[14px] font-bold text-white disabled:opacity-60"
                  disabled={signMutation.isPending}
                  onClick={doSubmit}
                >
                  {signMutation.isPending ? "제출 중…" : "제출하기"}
                </button>
              ) : (
                <button
                  type="button"
                  className="flex-1 rounded-xl bg-[#FF9934] py-2.5 text-[14px] font-bold text-white"
                  onClick={() => {
                    setShowSubmitConfirm(false);
                    guideToNextField();
                  }}
                >
                  미완료 항목으로 이동
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const Card = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="mx-4 my-3 bg-white rounded-2xl border border-[#EEE] p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
    <div className="mb-3 flex items-center gap-2">
      <span className="h-3.5 w-1 rounded-full bg-[#FF9934]" />
      <div className="text-[13px] font-bold text-[#333]">{title}</div>
    </div>
    {children}
  </div>
);

/* ---------- 서명 캔버스 ---------- */
const SignatureModal = ({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (dataUrl: string) => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#111";
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX =
      "touches" in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY =
      "touches" in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    drawing.current = true;
    const ctx = canvasRef.current!.getContext("2d")!;
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext("2d")!;
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };
  const end = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const save = () => {
    if (!hasDrawn) {
      alert("서명을 먼저 그려 주세요");
      return;
    }
    onSave(canvasRef.current!.toDataURL("image/png"));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center">
      <div className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-md p-5">
        <div className="text-[16px] font-bold mb-1">서명해 주세요</div>
        <div className="text-[12px] text-[#666] mb-3">
          아래 박스 안에 손가락이나 마우스로 서명해 주세요
        </div>
        <canvas
          ref={canvasRef}
          width={600}
          height={240}
          className="w-full border border-[#E9E9E9] rounded-lg touch-none bg-white"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={clear}
            className="flex-1 py-2.5 text-[12px] border border-[#E9E9E9] text-[#666] rounded-full"
          >
            지우기
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 text-[12px] border border-[#E9E9E9] text-[#666] rounded-full"
          >
            취소
          </button>
          <button
            type="button"
            onClick={save}
            className="flex-[2] py-2.5 text-[12px] font-semibold bg-[#FF9934] text-white rounded-full"
          >
            서명 사용하기
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------- 거절 모달 ---------- */
const RejectModal = ({
  reason,
  setReason,
  onClose,
  onSubmit,
}: {
  reason: string;
  setReason: (s: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center">
    <div className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-md p-5">
      <div className="text-[16px] font-bold mb-1">서명을 거절할까요?</div>
      <div className="text-[12px] text-[#666] mb-3">
        사업장에 거절 사유가 전달돼요
      </div>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="예: 위탁 기간을 조정하고 싶어요"
        className="w-full h-24 border border-[#E9E9E9] rounded-lg p-2.5 text-[13px] resize-none focus:outline-none focus:border-[#FF9934]"
      />
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 py-2.5 text-[12px] border border-[#E9E9E9] text-[#666] rounded-full"
        >
          취소
        </button>
        <button
          type="button"
          onClick={onSubmit}
          className="flex-[2] py-2.5 text-[12px] font-semibold bg-[#F24747] text-white rounded-full"
        >
          거절 보내기
        </button>
      </div>
    </div>
  </div>
);
